import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import threadsAPI from '../api/threads';
import sessionsAPI from '../api/sessions';
import './ThreadView.css';

const ThreadView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { userId } = useAuth();
  const [thread, setThread] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [sessions, setSessions] = useState([]);
  const [sessionsError, setSessionsError] = useState('');
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  const [hasPermission, setHasPermission] = useState(true);
  
  // Edit modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState('');
  
  // Form state for editing
  const [editLabel, setEditLabel] = useState('');
  const [editSystemPrompt, setEditSystemPrompt] = useState('');
  const [editFirstMessage, setEditFirstMessage] = useState('');
  const [editAllUsersToOneSession, setEditAllUsersToOneSession] = useState(false);
  const [editIsActive, setEditIsActive] = useState(true);
  
  // Share link state
  const [shareLink, setShareLink] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);

  const formatTimeAgo = (isoString) => {
    if (!isoString) return '';
    const now = new Date();
    const then = new Date(isoString);
    const seconds = Math.max(1, Math.floor((now.getTime() - then.getTime()) / 1000));

    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const weeks = Math.floor(days / 7);
    const months = Math.floor(days / 30);
    const years = Math.floor(days / 365);

    if (seconds < 60) return `${seconds}s ago`;
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    if (weeks < 5) return `${weeks}w ago`;
    if (months < 12) return `${months}mo ago`;
    return `${years}y ago`;
  };


  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  useEffect(() => {
    const loadThread = async () => {
      setIsLoading(true);
      setError('');
      try {
        const data = await threadsAPI.getThreadById(id);
        setThread(data || null);
        
        // Check if user has permission to access this thread
        if (data && data.owner_id !== userId) {
          setHasPermission(false);
          return;
        }
        
        if (data) {
          // Initialize form state with thread data
          setEditLabel(data.label || '');
          setEditSystemPrompt(data.system_prompt || '');
          setEditFirstMessage(data.first_message || '');
          setEditAllUsersToOneSession(data.all_users_to_one_session || false);
          setEditIsActive(data.is_active !== false);
          
          // Generate share link automatically
          const baseUrl = window.location.origin;
          const link = `${baseUrl}/share/chat/thread/${id}`;
          setShareLink(link);
        }
      } catch (err) {
        console.error('Failed to fetch thread:', err);
        setError('Failed to load thread');
      } finally {
        setIsLoading(false);
      }
    };
    if (id) {
      loadThread();
    }
  }, [id, userId]);

  useEffect(() => {
    const loadSessions = async () => {
      if (!id) return;
      setIsLoadingSessions(true);
      setSessionsError('');
      try {
        const data = await sessionsAPI.getSessionsByThread(id);
        setSessions(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Failed to fetch sessions:', err);
        setSessionsError('Failed to load sessions');
      } finally {
        setIsLoadingSessions(false);
      }
    };

    loadSessions();
  }, [id]);

  const handleOpenEdit = () => {
    // Initialize form state with thread data
    setEditLabel(thread.label || '');
    setEditSystemPrompt(thread.system_prompt || '');
    setEditFirstMessage(thread.first_message || '');
    setEditAllUsersToOneSession(thread.all_users_to_one_session || false);
    setEditIsActive(thread.is_active !== false);
    setUpdateError('');
    setIsEditModalOpen(true);
  };

  const handleCloseEdit = () => {
    setIsEditModalOpen(false);
    setUpdateError('');
  };

  const handleUpdateThread = async (e) => {
    e.preventDefault();
    if (!thread) return;
    
    setIsUpdating(true);
    setUpdateError('');
    try {
      const payload = {
        label: editLabel.trim() ? editLabel.trim() : null,
        system_prompt: editSystemPrompt.trim() ? editSystemPrompt.trim() : null,
        first_message: editFirstMessage.trim() ? editFirstMessage.trim() : null,
        all_users_to_one_session: editAllUsersToOneSession,
        is_active: editIsActive,
      };
      const updatedThread = await threadsAPI.updateThread(thread.id, payload);
      setThread(updatedThread);
      setIsEditModalOpen(false);
    } catch (err) {
      console.error('Failed to update thread:', err);
      setUpdateError(err?.message || 'Failed to update thread');
    } finally {
      setIsUpdating(false);
    }
  };

  if (!hasPermission) {
    return (
      <div className="content-area" style={{ paddingTop: 0 }}>
        <div className="permission-denied">
          <div className="permission-denied-content">
            <div className="permission-denied-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zM10 17l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/>
              </svg>
            </div>
            <h2>Access Denied</h2>
            <p>You don't have permission to view this thread.</p>
            <p>This thread belongs to another user.</p>
            <button 
              className="permission-denied-button"
              onClick={() => navigate('/dashboard')}
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="content-area" style={{ paddingTop: 0 }}>
      {isLoading && <div className="loading-inline" style={{ marginTop: 12 }}>Loading thread...</div>}
      {error && <div className="error-message" style={{ marginTop: 12 }}>{error}</div>}

      {!isLoading && !error && thread && (
        <>
          {/* Thread Details Section */}
          <div className="thread-details-section">
            <div className="thread-details-header">
              <h2 className="thread-details-title">Thread Details</h2>
            </div>
            
            {updateError && <div className="error-message">{updateError}</div>}
            
            <div className="thread-details-display">
              <div className="form-row">
                <label className="form-label">Label</label>
                <div className="form-display">{thread.label || 'No label'}</div>
                <div className="form-helper">Optional label or title for the conversation thread</div>
              </div>

              <div className="form-row">
                <label className="form-label">System prompt</label>
                <div className="form-display">{thread.system_prompt || 'No system prompt'}</div>
                <div className="form-helper">Instructions for how the assistant should behave</div>
              </div>

              <div className="form-row">
                <label className="form-label">First message</label>
                <div className="form-display">{thread.first_message || 'No first message'}</div>
                <div className="form-helper">Initial message to be sent by assistant in new sessions</div>
              </div>

              <div className="form-row">
                <label className="form-label">Settings</label>
                <div className="thread-settings-grid">
                  <div className="setting-item">
                    <div className="setting-label">All users to one session</div>
                    <div className={`setting-value ${thread.all_users_to_one_session ? 'active' : 'inactive'}`}>
                      {thread.all_users_to_one_session ? 'Yes' : 'No'}
                    </div>
                  </div>
                  <div className="setting-item">
                    <div className="setting-label">Active thread</div>
                    <div className={`setting-value ${thread.is_active ? 'active' : 'inactive'}`}>
                      {thread.is_active ? 'Yes' : 'No'}
                    </div>
                  </div>
                </div>
              </div>

              <div className="form-row">
                <label className="form-label">Share Link</label>
                <div className="share-link-container">
                  <div className="share-link-input-group">
                    <input
                      type="text"
                      className="form-input share-link-input"
                      value={shareLink}
                      readOnly
                      placeholder="Share link will be generated automatically"
                    />
                    <button
                      type="button"
                      className={`btn ${copySuccess ? 'btn-success' : 'btn-outline'}`}
                      onClick={copyToClipboard}
                      title="Copy to clipboard"
                      disabled={!shareLink}
                    >
                      {copySuccess ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  <div className="form-helper">
                    Share this link to allow others to join the conversation. They will be redirected to a chat session.
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-primary" onClick={handleOpenEdit}>
                  Edit Thread
                </button>
              </div>
            </div>
          </div>

          {/* Sessions Section */}
          <div className="sessions-section">
            <div className="sessions-header">
              <h3 className="sessions-title">Conversation Sessions</h3>
              <div className="sessions-count">{sessions.length} session{sessions.length === 1 ? '' : 's'}</div>
            </div>
            {sessionsError && <div className="error-message">{sessionsError}</div>}
            {isLoadingSessions ? (
              <div className="loading-inline">Loading sessions...</div>
            ) : (
              <div className="threads-grid">
                {sessions.map((session) => (
                  <div
                    key={session.id}
                    className="thread-card"
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(`/my/chat/session/${session.hash_id}/thread/${session.conversation_thread_id}`)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        navigate(`/my/chat/session/${session.hash_id}/thread/${session.conversation_thread_id}`);
                      }
                    }}
                  >
                    <div className="thread-card-body">
                      <div className="thread-title">{session.label || 'Untitled session'}</div>
                      <div className="thread-subtitle">{formatTimeAgo(session.created_at)}</div>
                      {Array.isArray(session.users) && session.users.length > 0 && (
                        <div className="pill-row" aria-label="session users">
                          {session.users.map((u) => (
                            <span key={u.id} className="pill">
                              <div className="pill-avatar">
                                {u.username ? u.username.charAt(0).toUpperCase() : 'U'}
                              </div>
                              {u.username}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Edit Thread Modal */}
      {isEditModalOpen && (
        <div className="modal-backdrop" onClick={handleCloseEdit}>
          <div className="modal modal-lg edit-thread-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Edit Thread</div>
              <button className="modal-close" onClick={handleCloseEdit}>×</button>
            </div>
            <div className="modal-content">
              {updateError && <div className="form-error">{updateError}</div>}
              <form onSubmit={handleUpdateThread} className="form edit-thread-form">
                <div className="form-row">
                  <label htmlFor="edit-thread-label" className="form-label">Label (optional)</label>
                  <input
                    id="edit-thread-label"
                    type="text"
                    className="form-input"
                    placeholder="e.g., Research: RAG evaluation plan"
                    value={editLabel}
                    onChange={(e) => setEditLabel(e.target.value)}
                    maxLength={500}
                    disabled={isUpdating}
                  />
                  <div className="form-helper">Up to 500 characters.</div>
                </div>

                <div className="form-row">
                  <label htmlFor="edit-thread-system-prompt" className="form-label">System prompt (optional)</label>
                  <textarea
                    id="edit-thread-system-prompt"
                    className="form-textarea"
                    placeholder="Provide instructions for how the assistant should behave..."
                    value={editSystemPrompt}
                    onChange={(e) => setEditSystemPrompt(e.target.value)}
                    rows={10}
                    disabled={isUpdating}
                  />
                  <div className="form-helper">Long-form guidance that applies to this thread.</div>
                </div>

                <div className="form-row form-row-with-checkboxes">
                  <div className="form-text-field">
                    <label htmlFor="edit-thread-first-message" className="form-label">First message (optional)</label>
                    <textarea
                      id="edit-thread-first-message"
                      className="form-textarea"
                      placeholder="Initial message to be sent by assistant in new sessions..."
                      value={editFirstMessage}
                      onChange={(e) => setEditFirstMessage(e.target.value)}
                      rows={4}
                      disabled={isUpdating}
                    />
                    <div className="form-helper">Message that will be sent automatically when a new session starts.</div>
                  </div>
                  <div className="form-checkboxes">
                    <div className="form-checkbox-group">
                      <div className="form-checkbox-row">
                        <input
                          type="checkbox"
                          id="edit-thread-all-users-to-one-session"
                          checked={editAllUsersToOneSession}
                          onChange={(e) => setEditAllUsersToOneSession(e.target.checked)}
                          disabled={isUpdating}
                        />
                        <label htmlFor="edit-thread-all-users-to-one-session" className="form-checkbox-label">
                          All users to one session
                        </label>
                      </div>
                      <div className="form-helper">Allow multiple users to join in one conversation session.</div>
                    </div>
                    <div className="form-checkbox-group">
                      <div className="form-checkbox-row">
                        <input
                          type="checkbox"
                          id="edit-thread-is-active"
                          checked={editIsActive}
                          onChange={(e) => setEditIsActive(e.target.checked)}
                          disabled={isUpdating}
                        />
                        <label htmlFor="edit-thread-is-active" className="form-checkbox-label">
                          Active thread
                        </label>
                      </div>
                      <div className="form-helper">Thread is active and can be used for conversations.</div>
                    </div>
                  </div>
                </div>

                <div className="form-actions">
                  <button type="button" className="btn btn-secondary" onClick={handleCloseEdit} disabled={isUpdating}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={isUpdating}>
                    {isUpdating ? 'Updating…' : 'Update thread'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ThreadView;


